import { EnquiryDetail, EnquiryDetailCustomField } from '../models/Enquiry';
import '../../App.css';
import { TimezoneOption } from '../../lookupConfiguration/selectors/TimezoneSelector';
import { jsPDF } from 'jspdf';
// import moment from 'moment';
import { CustomFieldByHierarchy } from '../../enquiryCustomFields/CustomFieldRepositores';
import { TaskDetails, TaskSummaryList } from '../../leadsDetail/LeadsDetailRepository';
import moment from 'moment';
import { formatDate, formatNumber } from '../../helpers/ChartHelper';
import { MatterTerminology } from '../../MatterTerminology';
import { convertMentionInCommentToHtml } from '../../helpers/commonHelper';

// const formatDate = (value: Date, format: string) => {
//     return moment(value).format(format);
// };

interface HistoryFileV2Props {
    enquiryDetail: EnquiryDetail;
    sortDirection: string | null;
    currency: string;
    timezone: TimezoneOption[];
    // tslint:disable-next-line: no-any
    leadDetailsHistory: TaskSummaryList;
    // tslint:disable-next-line: no-any
    onSuccess: (data: Blob) => void;
    customFieldByHierarchy?: CustomFieldByHierarchy[];
}

export const HistoryFileV2 = async (props: HistoryFileV2Props) => {
    const { enquiryDetail } = props;

    const convertDate = (date: Date | null) => {
        if (date) {
            return moment(date).format('DD-MMM-YYYY');
        }
        return null;
    };

    const getDetails = () => {
        const title = [enquiryDetail.title, enquiryDetail.firstName, enquiryDetail.lastName].filter(Boolean);

        const leftDetails: { label: string; value: string | null }[] = [
            { label: '', value: title.join(' ') },
            { label: 'Email', value: enquiryDetail.email },
            { label: 'Phone', value: enquiryDetail.phone },
            { label: 'Mobile', value: enquiryDetail.mobile },
            { label: 'Source', value: enquiryDetail.source },
            { label: 'Referrer', value: enquiryDetail.referrerName },
            { label: 'Area of Law', value: enquiryDetail.areaOfLaw },
            { label: 'Location', value: enquiryDetail.office },
        ];

        if (enquiryDetail.isClosed && enquiryDetail.outcomeDate) {
            leftDetails.push({ label: 'Date Closed', value: convertDate(enquiryDetail.outcomeDate) });
        }

        const leftDetailsMap = leftDetails
            .map((detail) => {
                if (detail.value) {
                    return `
                    <div class="rh-row">
                        ${detail.label ? `<div>${detail.label}:</div>` : ''}
                        <div${detail.label ? '' : ' class="rh-name"'}>${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const rightDetails: { label: string; value: string | null | Date }[] = [
            { label: 'Created', value: formatDate(enquiryDetail.createdDate, 'DD MMM YYYY hh:mm a') },
            ...(enquiryDetail.dateAssigned ? [{ label: 'Assigned', value: formatDate(enquiryDetail.dateAssigned, 'DD MMM YYYY hh:mm a') }] : []),
        ];

        const rightDetailsMap = rightDetails
            .map((detail) => {
                if (detail.value) {
                    return `
                    <div class="rh-row">
                        ${detail.label ? `<div>${detail.label}:</div>` : ''}
                        <div>${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const additionalDetails: { label: string; value: string | null }[] = [
            { label: 'Nature of Enquiry', value: enquiryDetail.natureOfEnquiry },
            { label: 'Source Notes', value: enquiryDetail.sourceNotes },
            { label: 'Internal Notes', value: enquiryDetail.internalNotes },
            { label: 'Event and Campaign', value: enquiryDetail.eventAndCampaignName },
            { label: 'Event and Campaign Notes', value: enquiryDetail.eventAndCampaignNotes },
        ];

        const additionalDetailsMap = additionalDetails
            .map((detail) => {
                if (detail.value) {
                    return `
                    <div class="rh-row">
                        <div>${detail.label}:</div>
                        <div style="text-align: justify;">${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        return `
            <div class="rh-hero">
                <div class="rh-eyebrow">Enquiry Details</div>
                <div
                    style="display: flex; flex-direction: column; gap: 5px;"
                >
                    <div
                        style="display: flex; justify-content: space-between; gap: 15px;"
                    >
                        <div style="display: flex; flex-direction: column; margin-top: 5px; flex: 1; gap: 5px;">
                            ${leftDetailsMap}
                        </div>
                        <div style="display: flex; flex-direction: column; margin-top: 5px; flex: 1; gap: 5px; align-items: flex-end;">
                            ${rightDetailsMap}
                        </div>
                    </div>
                    <div class="rh-stack">
                        ${additionalDetailsMap}
                    </div>
                </div>
            </div>
        `;
    };

    const getOrganisation = () => {
        const organisationDetails: { label: string; value: string | null }[] = [
            { label: 'Organization Name', value: enquiryDetail.organisation },
            { label: 'Position', value: enquiryDetail.positionAtOrganisation },
        ];

        const organisationDetailsMap = organisationDetails
            .map((detail) => {
                if (detail.value) {
                    return `
                    <div class="rh-row">
                        <div>${detail.label}:</div>
                        <div>${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
            
        if (!organisationDetailsMap) {
            return '';
        }

        return `
            <div class="rh-section">
                <div class="rh-h2">Organization</div>
                <div class="rh-stack">
                    ${organisationDetailsMap}
                </div>
            </div>
        `;
    };

    const getEnquiryValues = () => {
        const leftEnquiryValues: { label: string; value: string | null | number; valid: boolean }[] = [
            { label: 'Estimated Fees', value: enquiryDetail.estimatedFees || 0, valid: Boolean(enquiryDetail.estimatedFees) || Boolean(enquiryDetail.enquiryQuality) },
            {
                // style="vertical-align: middle;"
                label: 'Includes Taxes',
                value: `<input type="checkbox" ${enquiryDetail.isEstimatedFeesIncludesTax ? 'checked' : ''} />`,
                valid: Boolean(enquiryDetail.estimatedFees) || Boolean(enquiryDetail.enquiryQuality),
            },
            { label: 'Enquiry Quality', value: enquiryDetail.enquiryQuality || 0, valid: Boolean(enquiryDetail.estimatedFees) || Boolean(enquiryDetail.enquiryQuality) },
        ];

        const leftEnquiryValuesMap = leftEnquiryValues
            .map((value) => {
                if (value.valid) {
                    return `
                        <div class="rh-row" style="align-items: center;">
                            <div>${value.label}:</div>
                            <div>${value.value}</div>
                        </div>
                    `;
                }
                return '';
            })
            .join('');

        const rightEnquiryValues: { label: string; value: string | null | number; valid: boolean }[] = [
            {
                label: 'Estimated Disbs',
                value: enquiryDetail.estimatedDisbursements || 0,
                valid: Boolean(enquiryDetail.estimatedDisbursements) || Boolean(enquiryDetail.likelihoodToProceed),
            },
            {
                label: 'Includes Taxes',
                value: `<input type="checkbox" ${enquiryDetail.isEstimatedDisbursementsIncludesTax ? 'checked' : ''} />`,
                valid: Boolean(enquiryDetail.estimatedDisbursements) || Boolean(enquiryDetail.likelihoodToProceed),
            },
            {
                label: 'Likelihood to Proceed',
                value: enquiryDetail.likelihoodToProceed || 0,
                valid: Boolean(enquiryDetail.estimatedDisbursements) || Boolean(enquiryDetail.likelihoodToProceed),
            },
        ];

        const rightEnquiryValuesMap = rightEnquiryValues
            .map((value) => {
                if (value.valid) {
                    return `
                    <div class="rh-row" style="align-items: center;">
                        <div>${value.label}:</div>
                        <div>${value.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        return `
            <div class="rh-section">
                <div class="rh-h2">Enquiry Value</div>
                <div
                    style="display: flex; justify-content: space-between; gap: 15px;"
                >
                    <div style="display: flex; flex-direction: column; margin-top: 5px; flex: 1; gap: 5px;">
                        ${leftEnquiryValuesMap}
                    </div>
                    <div style="display: flex; flex-direction: column; margin-top: 5px; flex: 1; gap: 5px;">
                        ${rightEnquiryValuesMap}
                    </div>
                </div>
            </div>
        `;
    };

    const getClientDetails = () => {
        const title = [enquiryDetail.clientTitle, enquiryDetail.clientFirstName, enquiryDetail.clientLastName].filter(Boolean);

        const clientDetails: { label: string; value: string | null | number; valid: boolean }[] = [
            { label: 'Client Name', value: title.join(' '), valid: Boolean(title.length) },
            { label: 'Relationship to the Enquirer', value: enquiryDetail.clientRelationshipToEnquirer, valid: Boolean(enquiryDetail.clientRelationshipToEnquirer) },
            { label: 'Birthday', value: enquiryDetail.dOB ? formatDate(enquiryDetail.dOB, 'DD-MMM-YYYY') : null, valid: Boolean(enquiryDetail.dOB) },
            { label: 'Gender of Client', value: enquiryDetail.gender, valid: Boolean(enquiryDetail.gender) },
            { label: 'Email', value: enquiryDetail.clientEmail, valid: Boolean(enquiryDetail.clientEmail) },
            { label: 'Telephone', value: enquiryDetail.clientPhone, valid: Boolean(enquiryDetail.clientPhone) },
            { label: 'Mobile', value: enquiryDetail.clientMobile, valid: Boolean(enquiryDetail.clientMobile) },
        ];

        const clientDetailsMap = clientDetails
            .map((detail) => {
                if (detail.valid) {
                    return `
                    <div class="rh-row">
                        <div>${detail.label}:</div>
                        <div>${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const checkBoxList: { label: string; value: string | null | number; valid: boolean }[] = [
            {
                label: 'Marketing Consent',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.isMarketingConsent}" />`,
                valid: enquiryDetail.isMarketingConsent,
            },
            {
                label: `T&C's Approved`,
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.isTermsAndConditionsApproved}" />`,
                valid: enquiryDetail.isTermsAndConditionsApproved,
            },
            {
                label: 'Privacy Statement Provided',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.isPrivacyStatementProvided}" />`,
                valid: enquiryDetail.isPrivacyStatementProvided,
            },
            {
                label: 'Conflict Check Performed',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.isConflictCheckPerformed}" />`,
                valid: enquiryDetail.isConflictCheckPerformed,
            },
            { label: 'No Conflict', value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.isNoConflict}" />`, valid: enquiryDetail.isNoConflict },
            {
                label: 'Identity Check Performed',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.isIdentityCheckPerformed}" />`,
                valid: enquiryDetail.isIdentityCheckPerformed,
            },
        ];

        const checkBoxListMap = checkBoxList
            .map((item) => {
                if (item.valid) {
                    return `
                    <div style="display: flex; gap: 5px; grid-column: span 1; align-items: center;">
                        <div>${item.value}</div>
                        <div>${item.label}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const address: { label: string; value: string | null; valid: boolean }[] = [
            { label: 'Building Name:', value: enquiryDetail.addressName, valid: Boolean(enquiryDetail.addressName) },
            { label: 'Unit Level', value: enquiryDetail.floor, valid: Boolean(enquiryDetail.floor) },
            { label: 'Number', value: enquiryDetail.number, valid: Boolean(enquiryDetail.number) },
            { label: 'Street 1', value: enquiryDetail.street, valid: Boolean(enquiryDetail.street) },
            { label: 'Street 2', value: enquiryDetail.clientAddressStreet2, valid: Boolean(enquiryDetail.clientAddressStreet2) },
            { label: 'City/Suburb', value: enquiryDetail.suburb, valid: Boolean(enquiryDetail.suburb) },
            { label: 'State', value: enquiryDetail.state, valid: Boolean(enquiryDetail.state) },
            { label: 'Postal Code', value: enquiryDetail.postcode, valid: Boolean(enquiryDetail.postcode) },
            { label: 'Country', value: enquiryDetail.country, valid: Boolean(enquiryDetail.country) },
        ];

        const addressMap = address
            .map((item) => {
                if (item.valid) {
                    return `
                    <div class="rh-row">
                        <div>${item.label}:</div>
                        <div>${item.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const showAddress = address.some((item) => item.valid);
        
        const postalAddress: { label: string; value: string | null; valid: boolean }[] = [
            { label: 'PO Box Type', value: enquiryDetail.pOBoxType, valid: Boolean(enquiryDetail.pOBox || enquiryDetail.postalSuburb || enquiryDetail.postalState || enquiryDetail.postalPostCode || enquiryDetail.postalCountry) },
            { label: 'Number', value: enquiryDetail.pOBox, valid: Boolean(enquiryDetail.pOBox) },
            { label: 'Suburb', value: enquiryDetail.postalSuburb, valid: Boolean(enquiryDetail.postalSuburb) },
            { label: 'State', value: enquiryDetail.postalState, valid: Boolean(enquiryDetail.postalState) },
            { label: 'Postal Code', value: enquiryDetail.postalPostCode, valid: Boolean(enquiryDetail.postalPostCode) },
            { label: 'Country', value: enquiryDetail.postalCountry, valid: Boolean(enquiryDetail.postalCountry) },
        ];
        
        const postalAddressMap = postalAddress
            .map((item) => {
                if (item.valid) {
                    return `
                    <div class="rh-row">
                        <div>${item.label}:</div>
                        <div>${item.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
    
        const showPostalAddress = postalAddress.some((item) => item.valid);

        return `
            <div class="rh-section">
                <div class="rh-h2">Client Details</div>
                <div class="rh-stack">
                    ${clientDetailsMap}
                </div>
                <div style="display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 5px; gap: 5px;">
                    ${checkBoxListMap}
                </div>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); margin-top: 5px; gap: 5px;">
                    ${
                        showAddress ? `
                        <div style="grid-column: span 1; display: flex; flex-direction: column; gap: 5px;">
                            <div class="rh-h3">Main Address</div>
                            ${addressMap}
                        </div>
                        `
                        : ''
                    }
                    ${
                        showPostalAddress ? `
                        <div style="grid-column: span 1; display: flex; flex-direction: column; gap: 5px;">
                            <div class="rh-h3">Postal Address</div>
                            ${postalAddressMap}
                        </div>`
                        : ''
                    }
                </div>
            </div>
        `;
    };
    
    const getClientDetails2 = () => {
        const title = [enquiryDetail.secondClientTitle, enquiryDetail.secondClientFirstName, enquiryDetail.secondClientLastName].filter(Boolean);

        const clientDetails: { label: string; value: string | null | number; valid: boolean }[] = [
            { label: 'Client Name', value: title.join(' '), valid: Boolean(title.length) },
            { label: 'Relationship to the Enquirer', value: enquiryDetail.secondClientRelationshipToEnquirer, valid: Boolean(enquiryDetail.secondClientRelationshipToEnquirer) },
            { label: 'Birthday', value: enquiryDetail.secondClientDOB ? formatDate(enquiryDetail.secondClientDOB, 'DD-MMM-YYYY') : null, valid: Boolean(enquiryDetail.secondClientDOB) },
            { label: 'Gender of Client', value: enquiryDetail.secondClientGender, valid: Boolean(enquiryDetail.secondClientGender) },
            { label: 'Email', value: enquiryDetail.secondClientEmail, valid: Boolean(enquiryDetail.secondClientEmail) },
            { label: 'Telephone', value: enquiryDetail.secondClientPhone, valid: Boolean(enquiryDetail.secondClientPhone) },
            { label: 'Mobile', value: enquiryDetail.secondClientMobile, valid: Boolean(enquiryDetail.secondClientMobile) },
        ];

        const clientDetailsMap = clientDetails
            .map((detail) => {
                if (detail.valid) {
                    return `
                    <div class="rh-row">
                        <div>${detail.label}:</div>
                        <div>${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const checkBoxList: { label: string; value: string | null | number; valid: boolean }[] = [
            {
                label: 'Marketing Consent',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.secondClientIsMarketingConsent}" />`,
                valid: enquiryDetail.secondClientIsMarketingConsent,
            },
            {
                label: `T&C's Approved`,
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.secondClientIsTermsAndConditionsApproved}" />`,
                valid: enquiryDetail.secondClientIsTermsAndConditionsApproved,
            },
            {
                label: 'Privacy Statement Provided',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.secondClientIsPrivacyStatementProvided}" />`,
                valid: enquiryDetail.secondClientIsPrivacyStatementProvided,
            },
            {
                label: 'Conflict Check Performed',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.secondClientIsConflictCheckPerformed}" />`,
                valid: enquiryDetail.secondClientIsConflictCheckPerformed,
            },
            { 
                label: 'No Conflict', 
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.secondClientIsNoConflict}" />`, 
                valid: enquiryDetail.secondClientIsNoConflict },
            {
                label: 'Identity Check Performed',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.secondClientIsIdentityCheckPerformed}" />`,
                valid: enquiryDetail.secondClientIsIdentityCheckPerformed,
            },
        ];

        const checkBoxListMap = checkBoxList
            .map((item) => {
                if (item.valid) {
                    return `
                    <div style="display: flex; gap: 5px; grid-column: span 1; align-items: center;">
                        <div>${item.value}</div>
                        <div>${item.label}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const address: { label: string; value: string | null; valid: boolean }[] = [
            { label: 'Building Name:', value: enquiryDetail.secondClientAddressName, valid: Boolean(enquiryDetail.secondClientAddressName) },
            { label: 'Unit Level', value: enquiryDetail.secondClientFloor, valid: Boolean(enquiryDetail.secondClientFloor) },
            { label: 'Number', value: enquiryDetail.secondClientNumber, valid: Boolean(enquiryDetail.secondClientNumber) },
            { label: 'Street 1', value: enquiryDetail.secondClientStreet1, valid: Boolean(enquiryDetail.secondClientStreet1) },
            { label: 'Street 2', value: enquiryDetail.secondClientStreet2, valid: Boolean(enquiryDetail.secondClientStreet2) },
            { label: 'City/Suburb', value: enquiryDetail.secondClientSuburb, valid: Boolean(enquiryDetail.secondClientSuburb) },
            { label: 'State', value: enquiryDetail.secondClientState, valid: Boolean(enquiryDetail.secondClientState) },
            { label: 'Postal Code', value: enquiryDetail.secondClientPostCode, valid: Boolean(enquiryDetail.secondClientPostCode) },
            { label: 'Country', value: enquiryDetail.secondClientCountry, valid: Boolean(enquiryDetail.secondClientCountry) },
        ];

        const addressMap = address
            .map((item) => {
                if (item.valid) {
                    return `
                    <div class="rh-row">
                        <div>${item.label}:</div>
                        <div>${item.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const showAddress = address.some((item) => item.valid);
        
        const postalAddress: { label: string; value: string | null; valid: boolean }[] = [
            { label: 'PO Box Type', value: enquiryDetail.secondClientPOBoxType, valid: Boolean(enquiryDetail.secondClientPOBox || enquiryDetail.secondClientPostalSuburb || enquiryDetail.secondClientPostalState || enquiryDetail.secondClientPostalPostCode || enquiryDetail.secondClientPostalCountry) },
            { label: 'Number', value: enquiryDetail.secondClientPOBox, valid: Boolean(enquiryDetail.secondClientPOBox) },
            { label: 'Suburb', value: enquiryDetail.secondClientPostalSuburb, valid: Boolean(enquiryDetail.secondClientPostalSuburb) },
            { label: 'State', value: enquiryDetail.secondClientPostalState, valid: Boolean(enquiryDetail.secondClientPostalState) },
            { label: 'Postal Code', value: enquiryDetail.secondClientPostalPostCode, valid: Boolean(enquiryDetail.secondClientPostalPostCode) },
            { label: 'Country', value: enquiryDetail.secondClientPostalCountry, valid: Boolean(enquiryDetail.secondClientPostalCountry) },
        ];
        
        const postalAddressMap = postalAddress
            .map((item) => {
                if (item.valid) {
                    return `
                    <div class="rh-row">
                        <div>${item.label}:</div>
                        <div>${item.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
    
        const showPostalAddress = postalAddress.some((item) => item.valid);

        return `
            <div class="rh-section">
                <div class="rh-h2">Client Details 2</div>
                <div class="rh-stack">
                    ${clientDetailsMap}
                </div>
                <div style="display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 5px; gap: 5px;">
                    ${checkBoxListMap}
                </div>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); margin-top: 5px; gap: 5px;">
                    ${
                        showAddress ? `
                        <div style="grid-column: span 1; display: flex; flex-direction: column; gap: 5px;">
                            <div class="rh-h3">Main Address</div>
                            ${addressMap}
                        </div>
                        `
                        : ''
                    }
                    ${
                        showPostalAddress ? `
                        <div style="grid-column: span 1; display: flex; flex-direction: column; gap: 5px;">
                            <div class="rh-h3">Postal Address</div>
                            ${postalAddressMap}
                        </div>`
                        : ''
                    }
                </div>
            </div>
        `;
    };
    
    const getRiskDetails = () => {
        
        const checkBoxList: { label: string; value: string | null | number; valid: boolean | null }[] = [
            {
                label: 'Risk Assessed',
                value: `<input type="checkbox" style="vertical-align: middle;" checked="${enquiryDetail.isRiskAssessed}" />`,
                valid: enquiryDetail.isRiskAssessed,
            },
            {
                label: 'Risk Assessed',
                value: `<input type="checkbox" />`,
                valid: !enquiryDetail.isRiskAssessed,
            }
        ];

        const checkBoxListMap = checkBoxList
            .map((item) => {
                if (item.valid) {
                    return `
                    <div style="display: flex; gap: 5px; grid-column: span 1; align-items: center;">
                        <div>${item.value}</div>
                        <div>${item.label}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
            
        const riskDetails: { label: string; value: string | null, valid: boolean | null }[] = [
            { label: 'Risk Level', value: enquiryDetail.riskLevel, valid: enquiryDetail.isRiskAssessed },
            { label: 'Risk Notes', value: enquiryDetail.riskNotes, valid: enquiryDetail.isRiskAssessed },
        ];

        const riskDetailsMap = riskDetails
            .map((detail) => {
                if (detail.value) {
                    return `
                    <div class="rh-row">
                        <div>${detail.label}:</div>
                        <div style="text-align: justify;">${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
            
        return `
            <div class="rh-section">
                <div class="rh-h2">Risk Details</div>
                <div style="display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 5px; gap: 5px;">
                    ${checkBoxListMap}
                </div>
                <div class="rh-stack">
                    ${riskDetailsMap}
                </div>
            </div>
        `;
    };
    
    const getOtherPartyDetails = () => {
        
        const title = [enquiryDetail.otherPartyTitle, enquiryDetail.otherPartyFirstName, enquiryDetail.otherPartyLastName].filter(Boolean);
        
        const otherDetails: { label: string; value: string | null, valid: boolean | null }[] = [
            {
                label: 'Name',
                value: title.join(' '),
                valid: Boolean(title.length),
            },
            {
                label: 'Email',
                value: enquiryDetail.otherPartyEmail,
                valid: Boolean(enquiryDetail.otherPartyEmail),
            },
            {
                label: 'Telephone',
                value: enquiryDetail.otherPartyPhone,
                valid: Boolean(enquiryDetail.otherPartyPhone),
            },
            {
                label: 'Birthday',
                value: enquiryDetail.otherPartyDOB ? formatDate(enquiryDetail.otherPartyDOB, 'DD-MMM-YYYY') : null,
                valid: Boolean(enquiryDetail.otherPartyDOB),
            },
            {
                label: 'Mobile',
                value: enquiryDetail.otherPartyMobile,
                valid: Boolean(enquiryDetail.otherPartyMobile),
            },
            {
                label: 'Notes',
                value: enquiryDetail.otherPartyNotes,
                valid: Boolean(enquiryDetail.otherPartyNotes),
            }
        ];
        
        const otherDetailsMap = otherDetails
            .map((detail) => {
                if (detail.value) {
                    return `
                    <div class="rh-row">
                        <div>${detail.label}:</div>
                        <div style="text-align: justify;">${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
        
        const address: { label: string; value: string | null; valid: boolean }[] = [
            { label: 'Building Name:', value: enquiryDetail.otherPartyAddressName, valid: Boolean(enquiryDetail.otherPartyAddressName) },
            { label: 'Unit Level', value: enquiryDetail.otherPartyFloor, valid: Boolean(enquiryDetail.otherPartyFloor) },
            { label: 'Number', value: enquiryDetail.otherPartyNumber, valid: Boolean(enquiryDetail.otherPartyNumber) },
            { label: 'Street 1', value: enquiryDetail.otherPartyStreet, valid: Boolean(enquiryDetail.otherPartyStreet) },
            { label: 'Street 2', value: enquiryDetail.otherPartyStreet2, valid: Boolean(enquiryDetail.otherPartyStreet2) },
            { label: 'City/Suburb', value: enquiryDetail.otherPartySuburb, valid: Boolean(enquiryDetail.otherPartySuburb) },
            { label: 'State', value: enquiryDetail.otherPartyState, valid: Boolean(enquiryDetail.otherPartyState) },
            { label: 'Postal Code', value: enquiryDetail.otherPartyPostcode, valid: Boolean(enquiryDetail.otherPartyPostcode) },
            { label: 'Country', value: enquiryDetail.otherPartyCountry, valid: Boolean(enquiryDetail.otherPartyCountry) },
        ];

        const addressMap = address
            .map((item) => {
                if (item.valid) {
                    return `
                    <div class="rh-row">
                        <div>${item.label}:</div>
                        <div>${item.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');

        const showAddress = address.some((item) => item.valid);
        
        const postalAddress: { label: string; value: string | null; valid: boolean }[] = [
            { label: 'PO Box Type', value: enquiryDetail.otherPartyPOBoxType, valid: Boolean(enquiryDetail.otherPartyPOBox || enquiryDetail.otherPartyPostalSuburb || enquiryDetail.otherPartyPostalState || enquiryDetail.otherPartyPostalPostCode || enquiryDetail.otherPartyPostalCountry) },
            { label: 'Number', value: enquiryDetail.otherPartyPOBox, valid: Boolean(enquiryDetail.otherPartyPOBox) },
            { label: 'Suburb', value: enquiryDetail.otherPartyPostalSuburb, valid: Boolean(enquiryDetail.otherPartyPostalSuburb) },
            { label: 'State', value: enquiryDetail.otherPartyPostalState, valid: Boolean(enquiryDetail.otherPartyPostalState) },
            { label: 'Postal Code', value: enquiryDetail.otherPartyPostalPostCode, valid: Boolean(enquiryDetail.otherPartyPostalPostCode) },
            { label: 'Country', value: enquiryDetail.otherPartyPostalCountry, valid: Boolean(enquiryDetail.otherPartyPostalCountry) },
        ];
        
        const postalAddressMap = postalAddress
            .map((item) => {
                if (item.valid) {
                    return `
                    <div class="rh-row">
                        <div>${item.label}:</div>
                        <div>${item.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
    
        const showPostalAddress = postalAddress.some((item) => item.valid);
        
        // show only other party details if any of the details exist to avoid showing empty section
        if (!otherDetails.some((detail) => detail.valid) && !showAddress && !showPostalAddress) {
            return '';
        }
        
        return `
            <div class="rh-section">
                <div class="rh-h2">Other Party</div>
                <div class="rh-stack">
                    ${otherDetailsMap}
                </div>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); margin-top: 5px; gap: 5px;">
                    ${
                        showAddress ? `
                        <div style="grid-column: span 1; display: flex; flex-direction: column; gap: 5px;">
                            <div class="rh-h3">Main Address</div>
                            ${addressMap}
                        </div>
                        `
                        : ''
                    }
                    ${
                        showPostalAddress ? `
                        <div style="grid-column: span 1; display: flex; flex-direction: column; gap: 5px;">
                            <div class="rh-h3">Postal Address</div>
                            ${postalAddressMap}
                        </div>`
                        : ''
                    }
                </div>
            </div>
        `;
    };
    
    const getDetailedEnquiryNotes = () => {
        
        const enquiryNotesDetails: { label: string; value: string | null, valid: boolean | null }[] = [
            {
                label: 'Notes',
                value: enquiryDetail.responseText,
                valid: Boolean(enquiryDetail.responseText),
            },
        ];
        
        const enquiryNotesDetailsMap = enquiryNotesDetails
            .map((detail) => {
                if (detail.value) {
                    return `
                    <div class="rh-row">
                        <div>${detail.label}:</div>
                        <div style="text-align: justify;">${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
            
        if (!enquiryNotesDetailsMap) {
            return '';
        }
        
        return `
            <div class="rh-section">
                <div class="rh-h2">Detailed Enquiry Notes</div>
                <div class="rh-stack">
                    ${enquiryNotesDetailsMap}
                </div>
                
            </div>
        `;
    };
    
    const getDocumentDetails = () => {
        
        const documentListsMap = enquiryDetail.files.map((file) => {
            return `
                <li>${file.name}</li>
            `;
        }).join('');
        
        if (!documentListsMap) {
            return '';
        }
        
        return `
            <div class="rh-section">
                <div class="rh-h2">Documents</div>
                <ul class="rh-list">
                    ${documentListsMap}
                </ul>
            </div>
        `;
    };
    
    const getAnswer = (matchingDetail : EnquiryDetailCustomField | undefined, currency: string) => {
        let answer = null;
    
        if (typeof matchingDetail?.boolValue === 'boolean') {
            answer = matchingDetail.boolValue ? 'Yes' : 'No';
        } 
        else if (matchingDetail?.currencyValue !== null) {
            answer =  currency + formatNumber(matchingDetail?.currencyValue ?? 0);
        } 
        else if (matchingDetail?.dateTimeValue !== null) {
            answer = formatDate(matchingDetail?.dateTimeValue, 'DD MMM YYYY hh:mm a')
        } 
        else if (matchingDetail?.dateValue !== null) {
            answer = formatDate(matchingDetail?.dateValue, 'DD MMM YYYY')
        } 
        else if (matchingDetail?.integerValue !== null) {
            answer = matchingDetail?.integerValue
        } 
        else if (matchingDetail?.multiLineTextValue !== null) {
            answer = matchingDetail?.multiLineTextValue;
        } 
        else if (matchingDetail?.numberValue !== null) {
            answer =  matchingDetail?.numberValue
        } 
        else if (matchingDetail?.textValue !== null) {
            answer = matchingDetail?.textValue;
        }    
        else if (matchingDetail?.lookupListName !== null) {
            answer = matchingDetail?.lookupListName;
        } else if (matchingDetail?.emailValue !== null) {
            answer = matchingDetail?.emailValue;
        }
    
        return answer;
    };
    
    const getCustomFieldDetails = () => {
        let customFieldItem = '';
        
        if (props.customFieldByHierarchy && props.customFieldByHierarchy.length > 0) {
            props.customFieldByHierarchy.forEach(group => {
                const filteredFields = group.customField.filter(filteredDetail => {
                    const matchingDetail = props.enquiryDetail.customFields.find(enquiry => enquiry.customFieldGuid === filteredDetail.guid);
                    return matchingDetail;
                });

                if (filteredFields.length > 0) {
                    
                    customFieldItem += 
                    `
                        <div class="rh-section">
                            <div class="rh-h2">${group.customFieldGroupName}</div>
                            <ul class="rh-list">
                                ${filteredFields.map(customFieldAnswer => {
                                    if (customFieldAnswer?.isActive) {
                                        const matchingDetail = props.enquiryDetail.customFields.find(detail => detail.customFieldGuid === customFieldAnswer.guid);
                                        const answer = getAnswer(matchingDetail, props.currency)
                                        return `<li style="text-align: justify;">${customFieldAnswer.description} : ${answer}</li>`;
                                    }
                                    return '';
                                }).join('')}
                            </ul>
                        </div>
                    `;
                }
            });
        }
                
        return customFieldItem;
    };
    
    const getDigitalTrackingDetails = () => {
        
        const trackingDetails: { label: string; value: string | null | number; valid: boolean }[] = [
            { label: 'Click Through Url', value: enquiryDetail.clickThroughUrl, valid: Boolean(enquiryDetail.clickThroughUrl) },
            { label: 'UTM Source', value: enquiryDetail.utmSource, valid: Boolean(enquiryDetail.utmSource) },
            { label: 'UTM Medium', value: enquiryDetail.utmMedium, valid: Boolean(enquiryDetail.utmMedium) },
            { label: 'UTM Campaign', value: enquiryDetail.utmCampaign, valid: Boolean(enquiryDetail.utmCampaign) },
            { label: 'UTM utmContent', value: enquiryDetail.utmContent, valid: Boolean(enquiryDetail.utmContent) },
            { label: 'UTM Keyword', value: enquiryDetail.utmKeyword, valid: Boolean(enquiryDetail.utmKeyword) },
            { label: 'GCLID', value: enquiryDetail.googleClickIdentifier, valid: Boolean(enquiryDetail.googleClickIdentifier) },
            { label: 'FBCLID', value: enquiryDetail.facebookClickIdentifier, valid: Boolean(enquiryDetail.facebookClickIdentifier) },
        ];

        const trackingDetailsMap = trackingDetails
            .map((detail) => {
                if (detail.valid) {
                    return `
                    <div class="rh-row">
                        <div>${detail.label}:</div>
                        <div>${detail.value}</div>
                    </div>
                `;
                }
                return '';
            })
            .join('');
            
        // only show if there is at least 1 valid tracking detail
        if (!trackingDetails.some((item) => item.valid)) {
            return '';
        }
        
        return `
            <div class="rh-section">
                <div class="rh-h2">Digital Tracking</div>
                <div class="rh-stack">
                    ${trackingDetailsMap}
                </div>
            </div>
        `;
    };
    
    const convertToLocalTimeZone = (date: string) => {
        const oldDate = new Date(date);
        const newDate = new Date(date);
        newDate.setMinutes(oldDate.getMinutes() - oldDate.getTimezoneOffset());
        return newDate;
    };

    const getDescription = (task: TaskDetails) => {
        let actionedDescription = task.actionedDescription && task.actionedDescription.length > 0 
            ? task.actionedDescription 
            : undefined;
    
        if (task.taskTypeDescription.toLowerCase() === 'enquiry follow up') {
            if (task.followUpAction && task.followUpAction.toLowerCase() === 're-open enquiry') {
                return actionedDescription ? actionedDescription : 'Re-Opened';
            } else if (task.completedDate) {
                return actionedDescription ? actionedDescription : task.followUpAction;
            } else if (task.dueDate) {
                return actionedDescription ? actionedDescription : 'Follow Up Due';
            } else {
                return actionedDescription ? actionedDescription : task.followUpAction;
            }
        } else {
            const enquiryText = task.isKeyOpportunity ? MatterTerminology.KeyOpportunity : MatterTerminology.Enquiry;
    
            if (task.enquiryLinkedMatter) {
                return 'Link Matter';
            }
    
            const taskTypeDescription = task.taskTypeDescription.replace('Enquiry', enquiryText);
            return taskTypeDescription;
        }
    };
    
    const getHistoryDate = (task: TaskDetails) => {
        if (task.taskTypeInternalDescription === 'BookMeeting' && task.updatedDate) {
            return formatDate(task.updatedDate, 'Do MMM YYYY hh:mm a');
        }
    
        if (task.updatedDate) {
            return formatDate(task.updatedDate, 'Do MMM YYYY hh:mm a');
        } else if (task.dueDateUTC) {
            return formatDate(convertToLocalTimeZone(task.dueDateUTC.toString()), 'Do MMM YYYY hh:mm a');
        } else {
            // this is Due before not current date
            return formatDate(new Date(), 'Do MMM YYYY hh:mm a');
        }
    
    };
    
    const convertToList = (validItems: { label: string | null; valid: boolean }[]) => {
        return validItems.length
            ? `
                <ul class="rh-list">
                    ${validItems.map((item) => `<li style="text-align: justify;">${item.label}</li>`).join('')}
                </ul>
            `
            : '';
    };
    
    const getHistoryDetailContent = (task: TaskDetails, timezone: TimezoneOption[], currency: string, enquiryDetail?: EnquiryDetail) => {
        const uiUtcOffset = new Date().getTimezoneOffset() * -1;

        let uiTimeZoneDescription = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (timezone) {
            let uiTimeZone = timezone.find((item: TimezoneOption) => {
                return item.baseUtcOffset === uiUtcOffset;
            });

            if (uiTimeZone) {
                uiTimeZoneDescription = uiTimeZone.timezoneValue;
            }

            if (task.taskTypeDescription.toLowerCase() === 'assign enquiry' && task.assignedToGuidID) {
                const validItems: { label: string; valid: boolean }[] = [
                    { label: `Assigned to ${task.assignedToName} by ${task.assignedByName}`, valid: Boolean(task.assignedToName && task.assignedByName) },
                    { label: convertMentionInCommentToHtml(task.followUpNotes || ''), valid: Boolean(task.followUpNotes) },
                ].filter((item) => item.valid);

                return convertToList(validItems);
                
            } else if (task.enquiryLinkedMatter || task.taskTypeDescription.toLowerCase() === 'link matter') {
                const validItems: { label: string | null; valid: boolean }[] = [
                    {
                        label: task.assignedByName,
                        valid: Boolean(task.assignedByName),
                    },
                    {
                        label: `${task.linkedMatterClient ? task.linkedMatterClient : ''} - ${task.linkedMatterTitle} (${task.enquiryLinkedMatter})`,
                        valid: Boolean(task.enquiryLinkedMatter),
                    },
                    {
                        label: 'Matter synching is in progress. Please check again after a short while.',
                        valid: !Boolean(task.enquiryLinkedMatter),
                    },
                ].filter((item) => item.valid);

                return convertToList(validItems);
                
            } else if (task.taskTypeInternalDescription === 'BookMeeting') {
                const validItems: { label: string; valid: boolean }[] = [
                    {
                        label: `Meeting scheduled at ${task.firmOffice} ${task.bookMeetingStartDateUTC ? `on ${moment(convertToLocalTimeZone(task.bookMeetingStartDateUTC.toString())).format('DD MMM YYYY hh:mm a')}` : ''} (${task.timezone ? uiTimeZoneDescription : ''})`,
                        valid: Boolean(task.isAssignedToTeam && task.assignedToName),
                    },
                    {
                        label: `Meeting with ${task.staff}${task.firmOffice ? ` at ${task.firmOffice}` : ''}${task.bookMeetingStartDateUTC ? ` on ${moment(convertToLocalTimeZone(task.bookMeetingStartDateUTC.toString())).format('DD MMM YYYY hh:mm a')}` : ''}${task.timezone ? ` (${uiTimeZoneDescription})` : ''}`,
                        valid: Boolean(!task.isAssignedToTeam && task.staff),
                    },
                    {
                        label: `Meeting Notes: ${task.meetingNotes}`,
                        valid: Boolean(task.meetingNotes),
                    },
                    {
                        label: `Confirmation Sent: ${task.sendConfirmation === true ? 'Yes' : 'No'}`,
                        valid: Boolean(task.sendConfirmation && !task.isAssignedToTeam),
                    },
                    {
                        label: `Fee Applicable: ${currency}${formatNumber((task.feeApplicable ?? 0), 2)}`,
                        valid: task.feeApplicable !== null && task.feeApplicable !== undefined,
                    },
                    {
                        label: `Assigned To: ${task.assignedToName}`,
                        valid: Boolean(task.assignedToName && task.isAssignedToTeam),
                    },
                    {
                        label: convertMentionInCommentToHtml(task.followUpNotes || '', 'Follow Up Notes'),
                        valid: Boolean(task.followUpNotes),
                    },
                ].filter((item) => item.valid);

                return convertToList(validItems);
                
            } else if (task.taskTypeInternalDescription === 'MeetingReminder') {
                const validItems: { label: string; valid: boolean }[] = [
                    {
                        label: `Meeting reminder sent by ${task.isEmailContentExists ? 'Email' : task.isMessageContentExists ? 'SMS' : ''} to ${enquiryDetail?.name || enquiryDetail?.firstName + ' ' + enquiryDetail?.lastName}`,
                        valid: true,
                    },
                    {
                        label: `Created by: ${task.assignedByName}`,
                        valid: true
                    }
                ].filter((item) => item.valid);
                
                return convertToList(validItems);
                
            } else if (task.taskTypeInternalDescription === 'MeetingTask' && (task.followUpNotes || task.outcomeNotes)) {
                const validItems: { label: string | null; valid: boolean }[] = [
                    {
                        label: task.assignedByName,
                        valid: true,
                    },
                    {
                        label: convertMentionInCommentToHtml(task.followUpNotes || ''),
                        valid: Boolean(task.followUpNotes),
                    },
                    {
                        label: `Previous Notes: ${task.outcomeNotes} ${(task.createdDate ? ' (' + moment(task.createdDate).format('DD MMM YYYY') + ') ' : '')}`,
                        valid: Boolean(task.outcomeNotes),
                    },
                ].filter((item) => item.valid);
                
                return convertToList(validItems);
                
            } else if (task.systemAction === 'CreateEmail') {
                const validItems: { label: string | null; valid: boolean }[] = [
                    {
                        label: task.assignedByName,
                        valid: true
                    },
                    {
                        label: `Subject: ${task.emailSubject}`,
                        valid: Boolean(task.emailSubject),
                    },
                    {
                        label: `From: ${task.fromEmailAddress}`,
                        valid: Boolean(task.fromEmailAddress),
                    },
                    {
                        label: `To: ${task.toEmailAddresses}`,
                        valid: Boolean(task.toEmailAddresses),
                    },
                    {
                        label: `Cc: ${task.ccEmailAddresses}`,
                        valid: Boolean(task.ccEmailAddresses),
                    },
                    {
                        label: `Sent by: ${task.assignedByName}`,
                        valid: Boolean(task.assignedByName),
                    },
                    {
                        label: convertMentionInCommentToHtml(task.followUpNotes || '', 'Follow Up Notes'),
                        valid: Boolean(task.followUpNotes),
                    },
                    {
                        label: `Previous Notes: '${task.outcomeNotes} ${(task.createdDate !== null ? ' (' + moment(task.createdDate).format('DD MMM YYYY') + ') ' : '')}`,
                        valid: Boolean(task.outcomeNotes),
                    }
                ].filter((item) => item.valid);
                
                return convertToList(validItems);
                
            } else if (task.systemAction === 'CreateSMS') {
                const validItems: { label: string | null; valid: boolean }[] = [
                    {
                        label: task.assignedByName,
                        valid: true
                    },
                    {
                        label: `Template Name: ${task.emailTemplateName}`,
                        valid: Boolean(task.emailTemplateName),
                    },
                    {
                        label: `To: ${task.toMobileNumber}`,
                        valid: Boolean(task.toMobileNumber),
                    },
                    {
                        label: convertMentionInCommentToHtml(task.followUpNotes || '', 'Follow Up Notes'),
                        valid: Boolean(task.followUpNotes),
                    },
                    {
                        label: `Previous Notes: '${task.outcomeNotes} ${(task.createdDate !== null ? ' (' + moment(task.createdDate).format('DD MMM YYYY') + ') ' : '')}`,
                        valid: Boolean(task.outcomeNotes),
                    }
                ].filter((item) => item.valid);
                
                return convertToList(validItems);
                
            } else if (task.systemAction === 'SendDataForm'){
                const validItems: { label: string | null; valid: boolean }[] = [
                    {
                        label: 'Template Name: ' + task.emailTemplateName,
                        valid: true
                    },
                    {
                        label: 'Subject: ' + task.emailSubject,
                        valid: Boolean(task.emailSubject),
                    },
                    {
                        label: `From: ${task.fromEmailAddress}`,
                        valid: Boolean(task.fromEmailAddress),
                    },
                    {
                        label: `To: ${task.toEmailAddresses}`,
                        valid: Boolean(task.toEmailAddresses),
                    },
                    {
                        label: `Sent by: ${task.assignedByName}`,
                        valid: Boolean(task.assignedByName),
                    }
                ].filter((item) => item.valid);

                return convertToList(validItems)
            } else if ((task.dueDate && task.completedDate) && getDescription(task) !== 'Follow Up Due') {
                const validItems: { label: string | null; valid: boolean }[] = [
                    {
                        label: task.assignedByName,
                        valid: true,
                    },
                    {
                        label: `Filenumber: ${task.enquiryLinkedMatter}`,
                        valid: Boolean(task.enquiryLinkedMatter),
                    },
                    {
                        label: convertMentionInCommentToHtml(task.followUpNotes || '', 'Follow Up Notes'),
                        valid: Boolean(task.followUpNotes),
                    },
                    {
                        label: `Previous Notes: '${task.outcomeNotes} ${(task.createdDate !== null ? ' (' + moment(task.createdDate).format('DD MMM YYYY') + ') ' : '')}`,
                        valid: Boolean(task.outcomeNotes),
                    }
                ].filter((item) => item.valid);
                
                return convertToList(validItems);
                
            } else if (task.followUpNotes) {
                const validItems: { label: string | null; valid: boolean }[] = [
                    {
                        label: task.assignedByName,
                        valid: true,
                    },
                    {
                        label: `Filenumber: ${task.enquiryLinkedMatter}`,
                        valid: Boolean(task.enquiryLinkedMatter),
                    },
                    {
                        label: convertMentionInCommentToHtml(task.followUpNotes || ''),
                        valid: Boolean(task.followUpNotes),
                    }
                ].filter((item) => item.valid);
                
                return convertToList(validItems);
            } else if (getDescription(task) === 'Follow Up Due' && task.outcomeNotes) {
                const validItems: { label: string | null; valid: boolean }[] = [
                    {
                        label: task.assignedByName,
                        valid: true,
                    },
                    {
                        label: `Filenumber: ${task.enquiryLinkedMatter}`,
                        valid: Boolean(task.enquiryLinkedMatter),
                    },
                    {
                        label: `Sent by: ${task.outcomeNotes}`,
                        valid: Boolean(task.outcomeNotes),
                    } 
                ];  
                
                return convertToList(validItems);
            } else if (task.assignedByName && task.completedDate) {
                const validItems: { label: string | null; valid: boolean }[] = [
                    { label: task.assignedByName, valid: true },
                ].filter((item) => item.valid);
                
                return convertToList(validItems);
            }
        }
        return '';
    };
    
    const getHistoryActivity = (task: TaskDetails, timezone: TimezoneOption[], currency: string, index: number, enquiryDetail?: EnquiryDetail) => {
        const historyTitle = getDescription(task);
        const historyTitleDate = getHistoryDate(task);
        const historyDetailsContent = getHistoryDetailContent(task, timezone, currency, enquiryDetail);
    
        const historyDetails: { label: string; value: string | null | number; valid: boolean }[] = [
            { label: historyTitleDate, value: historyTitle, valid: Boolean(historyTitleDate && historyTitle) },
        ];
        
        const historyDetailsMap = historyDetails
            .map((detail) => {
                if (detail.valid) {
                    return `
                        <div class="rh-tl-head">
                            <div>${detail.label}</div>
                            <div>${detail.value}</div>
                        </div>
                    `;
                }
                return '';
            })
            .join('');
            
        return `
            <div class="rh-tl-item">
                <div class="rh-tl-dot"></div>
                ${historyDetailsMap}
                ${historyDetailsContent}
            </div>
        `;  
    };
    
    const getCreatedContent = () => {
        
        const historyDetails: { label: string; value: string | null | number; valid: boolean }[] = [
            { label: formatDate(enquiryDetail.createdDate, 'Do MMM YYYY hh:mm a'), value: 'Created', valid: Boolean(enquiryDetail.createdDate) },
        ];
        
        const historyDetailsMap = historyDetails
            .map((detail) => {
                if (detail.valid) {
                    return `
                        <div class="rh-tl-head">
                            <div>${detail.label}</div>
                            <div>${detail.value}</div>
                        </div>
                    `;
                }
                return '';
            })
            .join('');
            
        let areaOfLawTitle = enquiryDetail.areaOfLaw 
            ? enquiryDetail.source 
                ? enquiryDetail.areaOfLaw + ' (' + enquiryDetail.source.trim() + ')'
                : enquiryDetail.areaOfLaw
            : '';
        
        const areaOfLawList: { label: string; valid: boolean }[] = [
            { label: areaOfLawTitle, valid: Boolean(enquiryDetail.areaOfLaw) },
            { label: enquiryDetail.natureOfEnquiry, valid: Boolean(enquiryDetail.natureOfEnquiry) },
            { label: `Created by: ${enquiryDetail.createdByName}`, valid: Boolean(enquiryDetail.createdByName) },
        ];
        
        const areaOfLawListMap = areaOfLawList
            .map((item) => {
                if (item.valid) {
                    return `
                       <li>${item.label}</li>
                    `;
                }
                return '';
            })
            .join('');
            
        const showAreaOfLawList = areaOfLawList.some((item) => item.valid);
        
        return `
            <div class="rh-tl-item">
                <div class="rh-tl-dot"></div>
                ${historyDetailsMap}
                ${showAreaOfLawList ? `<ul class="rh-list">${areaOfLawListMap}</ul>` : ''}
            </div>
        `;  
    };
    
    const getHistoryDetails = () => {
        
        let historyContent = getCreatedContent();
        
        const taskList = props.leadDetailsHistory.tasks;

        const taskCreateClient = taskList.find((task: TaskDetails) => {
            return (task.taskTypeDescription.toLowerCase() === 'create client' || task.taskTypeDescription.toLowerCase() === 'create matter') 
                && task.taskStatusDescription.toLowerCase() === 'not started';
        });
        
        let newTasks = taskList;
        
        if (taskCreateClient) {
            const index = taskList.indexOf(taskCreateClient);
            newTasks.splice(index, 1);
        }
        
        if (taskCreateClient) {
            historyContent += getHistoryActivity(taskCreateClient, props.timezone, props.currency, 0, enquiryDetail);
        }
        
        newTasks.sort((a, b) => a.createdDate > b.createdDate ? 1 : -1).map((task: TaskDetails, index: number) => 
            historyContent += getHistoryActivity(task, props.timezone, props.currency, index, enquiryDetail)
        );
        
        return `
            <div>
                <div class="rh-h1">History</div>
                <div class="rh-timeline">
                    ${historyContent}
                </div>
            </div>
        `;
    };


    // Presentation only: every rule is scoped to .rhist (the report's root) and is applied while the report is in the page
    // for jsPDF/html2canvas to draw. The data, the sections and their order are produced exactly as before.
    const reportStyles = `
        .rhist { color: #1F2433; }
        .rhist .rh-hero { background: #F5F7FF; border: 1px solid #E1E7FF; border-radius: 8px; padding: 12px 14px; }
        .rhist .rh-eyebrow { font-size: 8px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #5173FF; margin: 0 0 6px 0; }
        .rhist .rh-name { font-size: 17px; font-weight: 700; line-height: 1.2; color: #1F2433; margin-bottom: 3px; }
        .rhist .rh-h1 { font-size: 13px; font-weight: 700; color: #1F2433; margin: 0 0 8px 0; padding-bottom: 5px; border-bottom: 2px solid #5173FF; }
        .rhist .rh-section { margin-top: 4px; }
        .rhist .rh-h2 { font-size: 8.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #5173FF; margin: 4px 0 6px 0; padding: 1px 0 1px 7px; border-left: 3px solid #5173FF; }
        .rhist .rh-h3 { font-size: 8px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #667085; margin: 4px 0; }
        .rhist .rh-stack { display: flex; flex-direction: column; gap: 5px; margin-top: 5px; }
        .rhist .rh-row { display: flex; gap: 6px; }
        .rhist .rh-row > div:first-child { color: #667085; flex: 0 0 auto; }
        .rhist .rh-row > div.rh-name:first-child { color: #1F2433; }
        .rhist .rh-list { margin: 0; padding: 0; list-style: none; color: #344054; }
        .rhist .rh-list li { position: relative; margin: 0 0 2px 0; padding-left: 12px; }
        .rhist .rh-list li::before { content: ''; position: absolute; left: 3px; top: 0.55em; width: 3px; height: 3px; border-radius: 50%; background: #98A2B3; }
        .rhist .rh-timeline { margin: 4px 0 0 5px; border-left: 2px solid #D9E0FF; }
        .rhist .rh-tl-item { position: relative; padding: 0 0 14px 16px; }
        .rhist .rh-tl-item:last-child { padding-bottom: 0; }
        .rhist .rh-tl-dot { box-sizing: border-box; position: absolute; left: -7px; top: 3px; width: 12px; height: 12px; border-radius: 50%; background: #5173FF; border: 2px solid #FFFFFF; }
        .rhist .rh-tl-head { display: flex; flex-direction: row-reverse; justify-content: space-between; align-items: baseline; gap: 12px; }
        .rhist .rh-tl-head > div:first-child { font-size: 8.5px; color: #667085; white-space: nowrap; }
        .rhist .rh-tl-head > div:last-child { font-size: 11px; font-weight: 700; color: #5173FF; }
        .rhist .rh-tl-item .rh-list { margin-top: 4px; }
        .rhist .rh-tl-item .rh-tl-created { margin-top: 4px; }
    `;

    const htmlContent = `
        <style>${reportStyles}</style>
        <div class="rhist" style="width: 400pt; font-family: 'Nunito', Arial, Helvetica, sans-serif; font-size: 10px; line-height: 1.4; display: flex; flex-direction: column; gap: 14px;">
            ${getDetails()}
            ${getOrganisation()}
            ${getEnquiryValues()}
            ${getClientDetails()}
            ${getClientDetails2()}
            ${getRiskDetails()}
            ${getOtherPartyDetails()}
            ${getDetailedEnquiryNotes()}
            ${getDocumentDetails()}
            ${getCustomFieldDetails()}
            ${getDigitalTrackingDetails()}
            ${getHistoryDetails()}
        </div>
    `;

    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    const doc = new jsPDF({
        orientation: 'p',
        format: 'a4',
        unit: 'pt',
    });

    await doc.html(container, {
        margin: [15, 25, 15, 25],
        x: 0,
        y: 0,
        autoPaging: 'text',
        width: 400,
        windowWidth: 400,
        callback(doc) {
            // i want to get the pdf as blob then return as part of the value as onSuccess
            const documentBlog = doc.output('blob');
            props.onSuccess(documentBlog);
            document.body.removeChild(container);
        },
    });

    
};
